import { useEffect, useMemo, useState } from 'react'
import { Info, RotateCcw, Save } from 'lucide-react'
import { apiClient } from '@/lib/api.ts'
import {
  Badge,
  Button,
  Card,
  ErrorState,
  Input,
  LoadingState,
  Tooltip,
} from '@/components/ui'
import type { ParameterDef, PlanParameterValue, ParamGroup } from './types.ts'

interface ParametersTabProps {
  planId: number
  /** Wird aufgerufen, nachdem die Werte gespeichert sind und der Backend-Recalc durchgelaufen ist. */
  onSaved: () => void
}

const GROUP_ORDER: ParamGroup[] = ['pipeline', 'kosten', 'steuern_cashflow', 'mehrjahr']

type FormValue = number | number[] | string | null

export function ParametersTab({ planId, onSaved }: ParametersTabProps) {
  const [defs, setDefs] = useState<ParameterDef[] | null>(null)
  const [stored, setStored] = useState<Record<string, number | number[]>>({})
  const [draft, setDraft] = useState<Record<string, FormValue>>({})
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [errorDetail, setErrorDetail] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      setLoading(true)
      setError(null)
      try {
        const [defsRes, valuesRes] = await Promise.all([
          apiClient.get<ParameterDef[]>('/liquidity-planning/parameter-defs'),
          apiClient.get<PlanParameterValue[]>(`/liquidity-planning/plans/${planId}/parameters`),
        ])
        if (cancelled) return
        const valueMap: Record<string, number | number[]> = {}
        for (const v of valuesRes.data) {
          if (v.value !== null) valueMap[v.key] = v.value
        }
        setDefs(defsRes.data)
        setStored(valueMap)
        setDraft(initializeDraft(defsRes.data, valueMap))
      } catch (err) {
        if (cancelled) return
        const e = err as { response?: { data?: unknown }; message?: string }
        setError(e?.message ?? 'Fehler beim Laden der Parameter')
        setErrorDetail(e?.response?.data ? JSON.stringify(e.response.data) : null)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    void load()
    return () => { cancelled = true }
  }, [planId])

  const grouped = useMemo(() => {
    if (!defs) return []
    const buckets = new Map<ParamGroup, { label: string; defs: ParameterDef[] }>()
    for (const def of defs) {
      const bucket = buckets.get(def.group) ?? { label: def.groupLabel, defs: [] }
      bucket.defs.push(def)
      buckets.set(def.group, bucket)
    }
    return GROUP_ORDER
      .filter((g) => buckets.has(g))
      .map((g) => ({ group: g, ...buckets.get(g)! }))
  }, [defs])

  const dirty = useMemo(() => {
    if (!defs) return false
    for (const def of defs) {
      const next = draft[def.key] ?? null
      const prev = stored[def.key] ?? null
      if (!valuesEqual(prev, next)) return true
    }
    return false
  }, [defs, draft, stored])

  const handleSave = async () => {
    setSaving(true)
    setError(null)
    setErrorDetail(null)
    try {
      const payload: Record<string, FormValue> = {}
      for (const def of defs ?? []) {
        payload[def.key] = draft[def.key] ?? null
      }
      await apiClient.put(`/liquidity-planning/plans/${planId}/parameters`, { values: payload })
      const newStored: Record<string, number | number[]> = {}
      for (const [k, v] of Object.entries(payload)) {
        if (v !== null && v !== undefined && typeof v !== 'string') newStored[k] = v
      }
      setStored(newStored)
      onSaved()
    } catch (err) {
      const e = err as { response?: { data?: unknown }; message?: string }
      setError(e?.message ?? 'Fehler beim Speichern')
      setErrorDetail(e?.response?.data ? JSON.stringify(e.response.data) : null)
    } finally {
      setSaving(false)
    }
  }

  const handleReset = () => {
    if (!defs) return
    setDraft(initializeDraft(defs, stored))
  }

  const handleClear = (key: string) => {
    setDraft((prev) => ({ ...prev, [key]: null }))
  }

  const handleSetDefault = (def: ParameterDef) => {
    setDraft((prev) => ({ ...prev, [def.key]: def.defaultValue }))
  }

  if (loading) return <LoadingState label="Parameter werden geladen…" />
  if (error) return <ErrorState title="Parameter konnten nicht geladen werden" description={error} details={errorDetail} />
  if (!defs) return null

  return (
    <div className="space-y-4">
      <Card>
        <Card.Header>
          <div className="flex justify-between items-center">
            <div>
              <span className="text-h5 block">Globale Plan-Parameter</span>
              <span className="text-small text-gray-500">
                Quoten und Prozentwerte, die auf alle Plan-Monate wirken. Leer lassen = nicht angewendet (Default-Verhalten).
              </span>
            </div>
            <div className="flex items-center gap-2">
              {dirty && (
                <Button variant="ghost" onClick={handleReset} disabled={saving}>
                  <RotateCcw className="size-4 mr-1" /> Zurücksetzen
                </Button>
              )}
              <Button variant="primary" onClick={handleSave} disabled={!dirty || saving}>
                <Save className="size-4 mr-1" /> {saving ? 'Speichere…' : 'Speichern & Neu berechnen'}
              </Button>
            </div>
          </div>
        </Card.Header>
      </Card>

      {grouped.map(({ group, label, defs: groupDefs }) => (
        <Card key={group}>
          <Card.Header>
            <span className="text-h6">{label}</span>
          </Card.Header>
          <Card.Body>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
              {groupDefs.map((def) => (
                <ParameterField
                  key={def.key}
                  def={def}
                  value={draft[def.key] ?? null}
                  onChange={(next) => setDraft((prev) => ({ ...prev, [def.key]: next }))}
                  onClear={() => handleClear(def.key)}
                  onSetDefault={() => handleSetDefault(def)}
                />
              ))}
            </div>
          </Card.Body>
        </Card>
      ))}
    </div>
  )
}

function ParameterField({
  def, value, onChange, onClear, onSetDefault,
}: {
  def: ParameterDef
  value: FormValue
  onChange: (v: FormValue) => void
  onClear: () => void
  onSetDefault: () => void
}) {
  const isSet = value !== null && value !== undefined
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between gap-2">
        <label className="text-small font-medium text-gray-700 flex items-center gap-1.5">
          {def.label}
          {!def.active && (
            <Tooltip content="Wert wird gespeichert, aber noch nicht im Recalc angewandt (greift erst, wenn die zugehörige Quelle / Mehrjahresplanung aktiv ist).">
              <Badge variant="warning" size="sm">noch nicht aktiv</Badge>
            </Tooltip>
          )}
          <Tooltip content={def.hint}>
            <Info className="size-3.5 text-gray-400" />
          </Tooltip>
        </label>
        <div className="flex items-center gap-1.5 text-caption">
          {!isSet && def.defaultValue !== null && (
            <button
              type="button"
              onClick={onSetDefault}
              className="text-brand-600 hover:underline"
            >
              {formatDefault(def)}
            </button>
          )}
          {isSet && (
            <button
              type="button"
              onClick={onClear}
              className="text-gray-500 hover:text-red-600 hover:underline"
            >
              Leeren
            </button>
          )}
        </div>
      </div>
      {def.kind === 'percent' ? (
        <PercentInput value={value as number | null} onChange={onChange} step={def.step ?? 0.01} />
      ) : def.kind === 'months' ? (
        <MonthsInput value={value as number | null} onChange={onChange} />
      ) : def.kind === 'string' ? (
        <StringInput value={value as string | null} onChange={onChange} placeholder={def.defaultValue as unknown as string ?? ''} />
      ) : (
        <DistributionInput value={value as number[] | null} onChange={onChange} step={def.step ?? 0.05} />
      )}
    </div>
  )
}

function StringInput({
  value, onChange, placeholder,
}: {
  value: string | null
  onChange: (v: string | null) => void
  placeholder?: string
}) {
  return (
    <Input
      type="text"
      value={value ?? ''}
      placeholder={placeholder ? `Default: ${placeholder}` : 'leer = nicht gesetzt'}
      onChange={(e) => {
        const raw = e.target.value
        onChange(raw === '' ? null : raw)
      }}
      className="w-32"
    />
  )
}

function MonthsInput({
  value, onChange,
}: {
  value: number | null
  onChange: (v: number | null) => void
}) {
  // Ganzzahliger Monatswert (0–12). Wird im Backend als Zahl gespeichert,
  // hier als <input type="number"> angezeigt.
  const display = value !== null && value !== undefined ? String(value) : ''
  return (
    <div className="flex items-center gap-1">
      <Input
        type="number"
        step="1"
        min="0"
        max="12"
        value={display}
        placeholder="leer = Default (2)"
        onChange={(e) => {
          const raw = e.target.value
          if (raw === '') { onChange(null); return }
          const num = Number(raw)
          if (Number.isFinite(num) && num >= 0 && num <= 12) onChange(Math.round(num))
        }}
        className="w-20"
      />
      <span className="text-caption text-gray-500">Monate</span>
    </div>
  )
}

function PercentInput({
  value, onChange, step,
}: {
  value: number | null
  onChange: (v: number | null) => void
  step: number
}) {
  // Anzeige in Prozent-Punkten (z.B. 30) statt Rohwert (0.30) — UX-konsistent
  // mit dem bestehenden "Faktor (%)"-Feld in der Plan-Toolbar.
  const display = value !== null ? (value * 100).toFixed(2).replace(/\.?0+$/, '') : ''
  return (
    <div className="flex items-center gap-1">
      <Input
        type="number"
        step={step * 100}
        value={display}
        placeholder="leer = nicht gesetzt"
        onChange={(e) => {
          const raw = e.target.value
          if (raw === '') { onChange(null); return }
          const num = Number(raw)
          if (Number.isFinite(num)) onChange(num / 100)
        }}
      />
      <span className="text-small text-gray-500">%</span>
    </div>
  )
}

function DistributionInput({
  value, onChange, step,
}: {
  value: number[] | null
  onChange: (v: number[] | null) => void
  step: number
}) {
  const labels = ['≤14 Tage', '≤30 Tage', '≤60 Tage', '90+ Tage']
  const values = value ?? [0, 0, 0, 0]
  const sumPct = values.reduce((s, v) => s + v, 0) * 100
  const updateAt = (idx: number, pct: number | null) => {
    const next = [...values]
    next[idx] = pct === null ? 0 : pct / 100
    if (next.every((v) => v === 0) && pct === null) {
      onChange(null)
      return
    }
    onChange(next)
  }
  return (
    <div className="space-y-1">
      <div className="grid grid-cols-4 gap-1">
        {labels.map((label, i) => (
          <div key={label}>
            <span className="text-caption text-gray-500 block">{label}</span>
            <div className="flex items-center gap-0.5">
              <Input
                type="number"
                step={step * 100}
                value={(values[i] ?? 0) * 100 || ''}
                placeholder="0"
                onChange={(e) => {
                  const raw = e.target.value
                  if (raw === '') { updateAt(i, null); return }
                  const num = Number(raw)
                  if (Number.isFinite(num)) updateAt(i, num)
                }}
              />
              <span className="text-caption text-gray-500">%</span>
            </div>
          </div>
        ))}
      </div>
      <div className="text-caption text-gray-500">
        Summe: <span className={sumPct > 0 && Math.abs(sumPct - 100) > 0.5 ? 'text-amber-600 font-medium' : ''}>
          {sumPct.toFixed(1)} %
        </span>
        {sumPct > 0 && Math.abs(sumPct - 100) > 0.5 && ' (sollte 100 % ergeben)'}
      </div>
    </div>
  )
}

function valuesEqual(a: FormValue, b: FormValue): boolean {
  if (a === null || b === null) return a === b
  if (typeof a === 'number' && typeof b === 'number') return a === b
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false
    return a.every((v, i) => v === b[i])
  }
  return false
}

function initializeDraft(
  defs: ParameterDef[],
  stored: Record<string, number | number[]>,
): Record<string, FormValue> {
  const out: Record<string, FormValue> = {}
  for (const def of defs) {
    out[def.key] = stored[def.key] ?? null
  }
  return out
}

function formatDefault(def: ParameterDef): string {
  if (def.defaultValue === null) return ''
  if (def.kind === 'percent') {
    return `Default: ${((def.defaultValue as number) * 100).toFixed(1).replace(/\.0$/, '')} %`
  }
  if (def.kind === 'months') {
    return `Default: ${def.defaultValue} ${def.defaultValue === 1 ? 'Monat' : 'Monate'}`
  }
  if (def.kind === 'string') {
    return `Default: ${def.defaultValue}`
  }
  const arr = def.defaultValue as number[]
  return `Default: ${arr.map((v) => `${(v * 100).toFixed(0)}%`).join(' / ')}`
}
